// MaturaSquaresSimilarityAreaClosedTask.js
// Zadanie maturalne – zamknięte: podobieństwo kwadratów, suma pól, skala długości.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";
import SquaresSimilarityDiagram from "../../../../components/diagrams/SquaresSimilarityDiagram";

const LETTERS = ["A", "B", "C", "D"];

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const gcd = (x, y) => (y === 0 ? x : gcd(y, x % y));
const lcm = (x, y) => (x * y) / gcd(x, y);

const isPerfectSquare = (n) => {
  if (n <= 0) return false;
  const r = Math.floor(Math.sqrt(n));
  return r * r === n;
};

/* ============================================================
   GENEROWANIE DANYCH (poprawnych i typowego błędu)
   ============================================================ */

function generateConsistentData() {
  // pętla, dopóki nie dostaniemy ładnych t i tFalse
  // (nie będą kwadratami, >1, S < 1000)
  while (true) {
    const k = randInt(2, 7);

    const A = 1 + k * k; // poprawne
    const B = 1 + k;     // typowy błąd ucznia

    const L = lcm(A, B);

    const maxN = Math.floor(999 / L);
    if (maxN < 1) continue;

    const n = randInt(1, maxN);
    const S = L * n;

    const t = S / A;       // poprawne x^2
    const tFalse = S / B;  // x^2 z błędnego dzielenia przez (1+k)

    // chcemy dodatnie, >1 i nie będące kwadratami,
    // żeby w odpowiedziach były pierwiastki z liczb niewymiernych
    if (
      t > 1 &&
      tFalse > 1 &&
      !isPerfectSquare(t) &&
      !isPerfectSquare(tFalse)
    ) {
      return { k, S, t, tFalse };
    }
  }
}

/* ============================================================
   OPCJE ODPOWIEDZI
   ============================================================ */

function buildOptions(t, tFalse) {
  const correct = `\\sqrt{${t}}`;

  // dokładnie to, o co prosiłeś:
  // √t (poprawna), t, √tFalse, tFalse
  const allRaw = [
    correct,            // poprawna
    `${t}`,             // brak pierwiastka
    `\\sqrt{${tFalse}}`,// błąd skali, ale pierwiastkowanie zrobione
    `${tFalse}`,        // błąd skali + brak pierwiastka
  ];

  const all = shuffle(allRaw);
  const correctIndex = all.indexOf(correct);

  return { optionsLatex: all, correctIndex };
}

/* ============================================================
   GENEROWANIE ZADANIA
   ============================================================ */

function generateJune2025Original() {
  return {
    type: "MaturaSquaresSimilarityAreaClosedTask",
    data: {
      k: 5,
      S: 78,
      t: 3,
      tFalse: 13,
      optionsLatex: ["\\sqrt{3}", "3", "\\sqrt{13}", "13"],
      correctIndex: 0,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_CZE",
      taskNumber: 21,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_CZE"
  ) {
    return generateJune2025Original();
  }

  const { k, S, t, tFalse } = generateConsistentData();

  const { optionsLatex, correctIndex } = buildOptions(t, tFalse);

  return {
    type: "MaturaSquaresSimilarityAreaClosedTask",
    data: { k, S, t, tFalse, optionsLatex, correctIndex },
  };
}

/* ============================================================
   RENDER TREŚCI ZADANIA
   ============================================================ */

export function renderTask({ data }) {
  const { k, S, optionsLatex } = data;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>

      <p>
        Kwadrat <InlineMath math={"K_2"} /> jest podobny do kwadratu{" "}
        <InlineMath math={"K_1"} /> w skali{" "}
        <InlineMath math={`${k}`} /> (zobacz rysunek). Suma pól tych
        kwadratów jest równa{" "}
        <InlineMath math={`${S}`} />.
      </p>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          margin: "16px 0",
        }}
      >
        <SquaresSimilarityDiagram scaleK={k} />
      </div>

      <p>
        Długość boku kwadratu <InlineMath math={"K_1"} /> jest równa:
      </p>

      <div style={{ marginTop: 8 }}>
        {optionsLatex.map((latex, idx) => (
          <div key={idx} style={{ marginBottom: 6 }}>
            <InlineMath math={`${LETTERS[idx]}.\\; ${latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   INPUT ABCD
   ============================================================ */

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const options = data.optionsLatex.map((_, idx) => ({
    value: idx,
    label: LETTERS[idx],
    textNode: LETTERS[idx],
  }));

  const handleChange = (idx) => {
    const latex = data.optionsLatex[idx];
    const label = LETTERS[idx];

    onChange?.({
      choiceIndex: idx,
      latex,
      label,
    });
  };

  return (
    <ABCDInput
      options={options}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

/* ============================================================
   TWOJA ODPOWIEDŹ
   ============================================================ */

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    !userAnswer.latex
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, latex } = userAnswer;

  return <InlineMath math={`${label}.\\; ${latex}`} />;
}

/* ============================================================
   CHECK / ANSWER
   ============================================================ */

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") return false;
  return userAnswer.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const letter = LETTERS[data.correctIndex];
  const latex = data.optionsLatex[data.correctIndex];
  return (
    <div
    ><p><InlineMath math={`${letter}.\\; ${latex}`} /></p>
    </div>
  );
}

/* ============================================================
   WYJAŚNIENIE – kroki jak w prostokącie
   ============================================================ */

export function renderExplanation({ data }) {
  const { k, S, t } = data;
  const k2 = k * k;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        <strong>Krok 1.</strong> Oznaczamy długość boku kwadratu{" "}
        <InlineMath math={"K_1"} /> przez <InlineMath math={"x"} />.
      </p>

      <p>
        Kwadrat <InlineMath math={"K_2"} /> jest podobny do kwadratu{" "}
        <InlineMath math={"K_1"} /> w skali{" "}
        <InlineMath math={`${k}`} />, więc jego bok ma długość{" "}
        <InlineMath math={`${k}x`} />.
      </p>

      <p>
        <strong>Krok 2.</strong> Zapisujemy pola obu kwadratów:
      </p>

      <BlockMath math={"P_{K_1} = x^2"} />
      <BlockMath
        math={`P_{K_2} = (${k}x)^2 = ${k}^2x^2 = ${k2}x^2`}
      />

      <p>
        <strong>Krok 3.</strong> Suma pól kwadratów jest równa{" "}
        <InlineMath math={`${S}`} />:
      </p>

      <BlockMath math={`x^2 + ${k2}x^2 = ${S}`} />
      <BlockMath math={`(1 + ${k2})x^2 = ${S}`} />

      <p>
        <strong>Krok 4.</strong> Rozwiązujemy równanie względem{" "}
        <InlineMath math={"x^2"} />:
      </p>

      <BlockMath math={`x^2 = \\frac{${S}}{1 + ${k2}} = ${t}`} />

      <p>
        <strong>Krok 5.</strong> Ponieważ <InlineMath math="x" /> jest długością boku,
        mamy <InlineMath math="x>0" />. Wybieramy dodatni pierwiastek:
      </p>

      <BlockMath math={`x = \\sqrt{${t}}`} />

      <p>
        Zatem długość boku kwadratu{" "}
        <InlineMath math={"K_1"} /> wynosi{" "}
        <InlineMath math={`\\sqrt{${t}}`} />.
      </p>
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  renderUserAnswer,
  checkAnswer,
  renderAnswer,
  renderExplanation,
};
